// src/components/SessionOverview.jsx
//
// The Overview tab (docs/slot-story-spec.md, "Session overview"): summary line,
// platform cards (2+ platforms), slot timeline with page-visit dividers, who won
// each unit, the demand funnel, and findings. Everything comes from
// utils/sessionOverview.js, which derives it from the per-slot story, so these
// numbers can never disagree with the Slots tab. Charts are drawn at the
// container's real pixel width (useWidth), so text never scales with the window.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildSessionOverview, buildFunnel, buildFindings, WINNER_BUCKETS } from '../utils/sessionOverview';

const WIN_STYLE = {
  sdk: { color: 'var(--rec-text)', label: 'SDK bidding' },
  prebid: { color: 'var(--primary)', label: 'Prebid' },
  gam: { color: 'var(--ink-2)', label: 'GAM / AdX' },
  filled: { color: 'var(--success-border)', label: 'Filled, winner unknown' },
  nofill: { color: 'var(--error-text)', label: 'No fill' },
  unknown: { color: 'var(--neutral-border)', label: 'Not visible (USB)' },
};
const FILL_COLOR = { filled: 'var(--success-text)', nofill: 'var(--error-text)' };
const KIND = {
  problem: { label: 'Problem', bg: 'var(--error-bg)', fg: 'var(--error-text)' },
  opportunity: { label: 'Opportunity', bg: 'var(--success-bg)', fg: 'var(--success-text)' },
  caveat: { label: 'Caveat', bg: 'var(--neutral-bg)', fg: 'var(--neutral-text)' },
};

const pad = (n) => String(n).padStart(2, '0');
const clock = (t, sec = true) => {
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}${sec ? `:${pad(d.getSeconds())}` : ''}`;
};
const pct = (x) => (x == null ? 'n/a' : `${Math.round(x * 100)}%`);

function niceStep(spanMs) {
  const steps = [1, 2, 5, 10, 30, 60, 120, 300, 600, 900, 1800, 3600].map((s) => s * 1000);
  return steps.find((s) => spanMs / s <= 8) || 7200 * 1000;
}

/** Container width in px; jsdom (no ResizeObserver) gets a fixed fallback. */
function useWidth(fallback = 960) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cw = Math.round(entries[0].contentRect.width);
      if (cw > 0) setW(cw);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Short page label: pageName as-is, URL -> "Home" or the last path segment. */
export function shortPage(page) {
  const p = String(page || '');
  if (!/^https?:\/\//i.test(p)) return p || 'Session';
  try {
    const u = new URL(p);
    const segs = u.pathname.split('/').filter(Boolean);
    if (!segs.length) return 'Home';
    const last = decodeURIComponent(segs[segs.length - 1]).replace(/[-_]+/g, ' ');
    return last.length > 22 ? `${last.slice(0, 21)}…` : last;
  } catch { return p; }
}

const panel = { border: '1px solid var(--line)', borderRadius: 10, background: 'var(--card)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 };
const h2 = { margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--ink)' };
const headRow = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px 16px' };

function Legend({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', fontSize: 12, color: 'var(--muted)' }}>
      {items.map(([label, color, shape]) => (
        <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: shape === 'sq' ? 2 : '50%', background: shape === 'hollow' ? 'transparent' : color, border: shape === 'hollow' ? `1.5px solid ${color}` : 'none' }} />
          {label}
        </span>
      ))}
    </div>
  );
}

function PlatformCards({ platforms }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }} data-testid="overview-platforms">
      {platforms.map((p) => {
        const rows = [
          ['Slots', p.slots],
          ['Fill (known)', p.fillRate == null ? 'n/a' : `${pct(p.fillRate)} of ${p.fillKnown}`],
          ['Prebid price sent (hb_pb)', `${p.prebidSent}/${p.slots}`, p.prebidSent === 0],
          ['APS bid sent (amznbid)', p.apsSent == null ? 'not visible (USB)' : `${p.apsSent}/${p.apsOf}`, p.apsSent === 0, p.apsSent == null],
          ['SDK bidding share of wins', p.winsKnown ? `${pct(p.sdkWins / p.winsKnown)} of ${p.winsKnown}` : 'n/a'],
          ['Duplicate bursts', p.bursts, p.bursts > 0],
        ];
        return (
          <div key={p.platform} style={{ ...panel, gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{p.platform}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'var(--neutral-bg)', color: 'var(--neutral-text)' }}>{p.capture}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', fontSize: 12 }}>
              {rows.map(([k, v, bad, na]) => (
                <React.Fragment key={k}>
                  <span style={{ color: 'var(--ink-2)' }}>{k}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', color: na ? 'var(--muted)' : bad ? 'var(--error-text)' : 'var(--ink)', fontWeight: bad ? 600 : 400 }}>{v}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Timeline({ data, visits, onShowSlot, onSelectVisit }) {
  const [ref, width] = useWidth();
  const { lanes, start, end } = data;
  const plats = [...new Set(lanes.map((l) => l.platform))];
  const multi = plats.length > 1;
  const W = Math.max(width, 520);
  const L = multi ? 170 : 110, R = 14, top = 30, lane = 34, gap = multi ? 12 : 0;
  const yOf = (i) => top + i * lane + plats.indexOf(lanes[i].platform) * gap + lane / 2;
  const bottom = lanes.length ? yOf(lanes.length - 1) + lane / 2 : top;
  const H = bottom + 26;
  const span = Math.max(end - start, 1000);
  const x = (t) => L + ((t - start) / span) * (W - L - R - 30);
  const step = niceStep(span);
  const ticks = [];
  for (let t = Math.ceil(start / step) * step; t <= end; t += step) ticks.push(t);
  // Each visit label gets the room up to the next divider; trimmed to fit, or
  // hidden when there is no room for a readable word.
  const inRange = visits.filter((v) => v.start >= start && v.start <= end);
  const shownVisits = inRange.map((v, i) => {
    const vx = x(v.start);
    const next = i + 1 < inRange.length ? x(inRange[i + 1].start) : W - R;
    const chars = Math.floor((next - vx - 10) / 6.4);
    const text = chars >= v.label.length ? v.label : chars >= 5 ? `${v.label.slice(0, chars - 1)}…` : null;
    return { ...v, vx, text };
  });
  return (
    <div ref={ref} style={{ width: '100%', overflow: 'auto', maxHeight: 440 }} data-testid="overview-timeline-box">
      <svg width={W} height={H} style={{ display: 'block' }} role="img" aria-label="GAM requests per ad unit over the session" data-testid="overview-timeline">
        {shownVisits.map((v, i) => (
          <g key={`${v.key}-${i}`}>
            <line x1={v.vx} x2={v.vx} y1={top - 12} y2={bottom} stroke="var(--line)" />
            {v.text ? (
              <text
                x={v.vx + 5}
                y={top - 15}
                fontSize={11}
                fill="var(--muted)"
                style={{ cursor: onSelectVisit ? 'pointer' : 'default' }}
                onClick={() => onSelectVisit && onSelectVisit(v.key)}
              >
                <title>{`${v.label}${v.visitLabel ? ` · ${v.visitLabel}` : ''} at ${clock(v.start)} — click to show only this page`}</title>
                {v.text}
              </text>
            ) : null}
          </g>
        ))}
        {lanes.map((ln, i) => {
          const y = yOf(i);
          const firstOfPlat = i === 0 || lanes[i - 1].platform !== ln.platform;
          const lx = multi ? 70 : 0;
          return (
            <g key={`${ln.platform}-${ln.unit}`}>
              <line x1={L} x2={W - R} y1={y} y2={y} stroke="var(--line)" strokeOpacity={0.6} />
              {multi && firstOfPlat ? <text x={0} y={y + 4} fontSize={10} fontWeight={700} fill="var(--muted)" letterSpacing="0.04em">{ln.platform.toUpperCase()}</text> : null}
              <text x={lx} y={y + 4} fontSize={12} fontWeight={600} fill="var(--ink)" style={{ fontFamily: 'var(--font-mono)' }}>{ln.unit}</text>
              {ln.marks.map((m) => {
                const cx = x(m.t);
                const title = m.burst
                  ? `${ln.unit}: ${m.count} requests in ${((m.last - m.t) / 1000).toFixed(1)} s from ${clock(m.t)} — likely duplicates. ${m.filledCount} filled.`
                  : `${ln.unit} at ${clock(m.t)} — ${m.fill === 'filled' ? `filled${m.winners[0] ? ` by ${m.winners[0]}` : ''}` : m.fill === 'nofill' ? 'no fill' : 'fill not visible'}${m.hbPb ? ` · hb_pb ${m.hbPb}` : ''}`;
                const open = () => onShowSlot && onShowSlot(m.keys[0]);
                return (
                  <g
                    key={m.keys[0]}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={open}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                    data-testid={m.burst ? 'overview-burst' : 'overview-mark'}
                  >
                    <title>{title}</title>
                    {m.burst ? (
                      <>
                        <rect x={cx - 6} y={y - 9} width={30} height={18} rx={9} fill="var(--error-bg)" stroke="var(--error-text)" />
                        <text x={cx + 9} y={y + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--error-text)">×{m.count}</text>
                      </>
                    ) : (
                      <circle cx={cx} cy={y} r={5.5} fill={FILL_COLOR[m.fill] || 'var(--card)'} stroke={FILL_COLOR[m.fill] ? 'var(--card)' : 'var(--muted)'} strokeWidth={1.5} />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
        {ticks.map((t) => (
          <text key={t} x={x(t)} y={H - 6} textAnchor="middle" fontSize={10} fill="var(--muted)" style={{ fontFamily: 'var(--font-mono)' }}>{clock(t, step < 60000)}</text>
        ))}
      </svg>
    </div>
  );
}

function WhoWon({ wins }) {
  const [ref, width] = useWidth(460);
  const multi = new Set(wins.map((w) => w.platform)).size > 1;
  const maxN = Math.max(1, ...wins.map((w) => w.total));
  const W = Math.max(width, 280), L = multi ? 150 : 80, R = 34, rowH = 26, H = wins.length * rowH + 2;
  const unitW = (W - L - R) / maxN;
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={W} height={H} style={{ display: 'block' }} role="img" aria-label="Requests per ad unit by winner" data-testid="overview-wins">
        {wins.map((w, i) => {
          const y = i * rowH + 4;
          let acc = L;
          return (
            <g key={`${w.platform}-${w.unit}`}>
              <text x={0} y={y + 12} fontSize={12} fontWeight={600} fill="var(--ink)" style={{ fontFamily: 'var(--font-mono)' }}>{w.unit}</text>
              {multi ? <text x={80} y={y + 12} fontSize={11} fill="var(--muted)">{w.platform}</text> : null}
              {WINNER_BUCKETS.map((b) => {
                const c = w.counts[b];
                if (!c) return null;
                const rx = acc;
                acc += c * unitW;
                return (
                  <rect key={b} x={rx} y={y} width={Math.max(c * unitW - 2, 2)} height={16} rx={3} fill={WIN_STYLE[b].color}>
                    <title>{`${w.unit}${multi ? ` (${w.platform})` : ''}: ${WIN_STYLE[b].label} ${c} of ${w.total}`}</title>
                  </rect>
                );
              })}
              <text x={acc + 5} y={y + 12} fontSize={11} fill="var(--ink-2)" style={{ fontFamily: 'var(--font-mono)' }}>{w.total}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Funnel({ funnel }) {
  const { cols, stages } = funnel;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }} data-testid="overview-funnel">
        <thead>
          <tr>
            <th style={{ textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'var(--muted)', padding: '0 12px 8px 0' }}>Stage</th>
            {cols.map((c) => <th key={c.label} style={{ textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'var(--muted)', padding: '0 0 8px 10px', whiteSpace: 'nowrap' }}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {stages.map((s) => (
            <tr key={s.id}>
              <td style={{ padding: '7px 12px 7px 0', borderTop: '1px solid var(--line)', color: 'var(--ink)' }}>{s.label}</td>
              {s.cells.map((c, i) => (
                <td key={i} style={{ padding: '7px 0 7px 10px', borderTop: '1px solid var(--line)', minWidth: 120 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 46px', alignItems: 'center', gap: 8 }}>
                    <div style={{ height: 6, borderRadius: 3, background: c.n == null ? 'repeating-linear-gradient(90deg, var(--line) 0 4px, transparent 4px 7px)' : 'var(--line)', overflow: 'hidden' }}>
                      {c.n != null ? <div style={{ height: '100%', width: `${(c.n / c.of) * 100}%`, background: 'var(--primary)', borderRadius: 3 }} /> : null}
                    </div>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right', color: c.n == null ? 'var(--muted)' : c.n === 0 && s.id !== 'requests' ? 'var(--error-text)' : 'var(--ink-2)', fontWeight: c.n === 0 ? 600 : 400 }} title={c.n == null ? 'Not visible in USB capture' : undefined}>
                      {c.n == null ? 'n/a' : `${c.n}/${c.of}`}
                    </span>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Findings({ findings, onShowSlot }) {
  if (!findings.length) return <div style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing stands out in this capture.</div>;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="overview-findings">
      {findings.map((f, i) => {
        const k = KIND[f.kind] || KIND.caveat;
        return (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 13, lineHeight: 1.45, color: 'var(--ink)' }}>
            <span style={{ flex: 'none', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 4, background: k.bg, color: k.fg }}>{k.label}</span>
            <span style={{ minWidth: 0 }}>
              {f.text}
              {f.keys && f.keys.length && onShowSlot ? (
                <button type="button" onClick={() => onShowSlot(f.keys[0])} style={{ marginLeft: 8, fontSize: 12, border: 'none', background: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0 }}>
                  Open slot{f.keys.length > 1 ? ` (1 of ${f.keys.length})` : ''}
                </button>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * @param {object[]} lifecycles
 * @param {object[]} visits  [{ key, label, start }] page visits for timeline dividers
 * @param {function} onShowSlot  (slotKey) -> open it in the Slots tab
 */
function PageChips({ visits, total, selected, onSelect }) {
  const chip = (active) => ({
    flex: 'none', fontSize: 12, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${active ? 'var(--primary)' : 'var(--line)'}`,
    background: active ? 'var(--primary)' : 'var(--card)', color: active ? 'white' : 'var(--ink-2)', fontWeight: active ? 600 : 400,
  });
  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }} role="group" aria-label="Page" data-testid="overview-pages">
      <button type="button" style={chip(selected == null)} aria-pressed={selected == null} onClick={() => onSelect(null)}>
        All pages · {total}
      </button>
      {visits.map((v) => (
        <button
          key={v.key}
          type="button"
          style={chip(selected === v.key)}
          aria-pressed={selected === v.key}
          title={`${v.page || v.label}${v.visitLabel ? ` · ${v.visitLabel}` : ''}`}
          onClick={() => onSelect(selected === v.key ? null : v.key)}
        >
          {v.label} <span style={{ opacity: 0.75, fontFamily: 'var(--font-mono)' }}>{clock(v.start, false)} · {v.slotKeys.length}</span>
        </button>
      ))}
    </div>
  );
}

const MISS_CODES = {
  'baseline-screen-slot-missing': { code: 'screen_slot_missing', field: 'slotsMissing', label: 'Expected slot never requested' },
  'baseline-screen-bidders-missing': { code: 'screen_bidder_missing', field: 'biddersMissing', label: 'Expected bidder not seen' },
  'baseline-screen-request-bloat': { code: 'screen_request_bloat', field: 'requestBloat', label: 'Request count over 2x expected' },
  'baseline-screen-consent-lost': { code: 'screen_consent_lost', field: 'consentLost', label: 'Consent signal lost' },
};

/**
 * Flatten the screen-expectation check results (proxy /proxy/checks/run results for
 * the baseline-screen-* family; each failure's row is a `screen` entity) into
 * [{ screen, code, label, severity, detail }]. Never throws; n/a / pass -> nothing,
 * so USB-unknown screens and packs with no expectations render no misses.
 */
export function buildExpectationMisses(results) {
  const out = [];
  try {
    for (const r of Array.isArray(results) ? results : []) {
      const meta = r && MISS_CODES[r.checkId || r.id];
      if (!meta || !Array.isArray(r.failures)) continue;
      const severity = r.severity === 'fail' || r.status === 'fail' ? 'fail' : 'warn';
      for (const f of r.failures) {
        const e = (f && f.row) || {};
        const v = e[meta.field];
        out.push({
          screen: e.screen || '(unknown screen)',
          code: meta.code,
          label: meta.label,
          severity,
          detail: Array.isArray(v) ? v.join(', ') : (meta.field === 'requestBloat' ? `${e.requestCount} requests vs p50 ${e.requestP50}` : ''),
        });
      }
    }
  } catch { /* never throw */ }
  return out;
}

function ExpectationMisses({ misses }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="overview-expectation-misses">
      {misses.map((m, i) => (
        <li key={`${m.screen}-${m.code}-${i}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'baseline', fontSize: 13 }} data-testid="overview-expectation-miss">
          <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: m.severity === 'fail' ? 'var(--error-bg)' : 'var(--neutral-bg)', color: m.severity === 'fail' ? 'var(--error-text)' : 'var(--neutral-text)' }}>{m.severity === 'fail' ? 'Fail' : 'Warn'}</span>
          <b style={{ color: 'var(--ink)' }}>{m.screen}</b>
          <span style={{ color: 'var(--ink-2)' }}>{m.label}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' }}>{m.code}{m.detail ? ` · ${m.detail}` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SessionOverview({ lifecycles, visits = [], onShowSlot, expectationMisses = [] }) {
  // Page filter: one visit's slots, or the whole session. Reset if that visit
  // disappears (new session, clear).
  const [selVisit, setSelVisit] = useState(null);
  const visit = selVisit != null ? visits.find((v) => v.key === selVisit) || null : null;
  const scoped = useMemo(() => {
    if (!visit) return lifecycles;
    const keys = new Set(visit.slotKeys);
    return (lifecycles || []).filter((lc) => lc && keys.has(lc.key));
  }, [lifecycles, visit]);
  const data = useMemo(() => buildSessionOverview(scoped), [scoped]);
  const funnel = useMemo(() => buildFunnel(data.facts), [data]);
  const findings = useMemo(() => buildFindings(data), [data]);
  const hasMisses = Array.isArray(expectationMisses) && expectationMisses.length > 0;
  if (!data.lanes.length) {
    // No GAM slots at all is exactly when expectation misses matter most.
    return hasMisses ? (
      <div style={{ padding: '4px 12px 16px' }} data-testid="session-overview">
        <section style={panel}>
          <div style={headRow}><h2 style={h2}>Screen expectation misses</h2></div>
          <ExpectationMisses misses={expectationMisses} />
        </section>
      </div>
    ) : null;
  }

  const usedBuckets = WINNER_BUCKETS.filter((b) => data.wins.some((w) => w.counts[b]));
  const known = data.facts.filter((f) => f.fill === 'filled' || f.fill === 'nofill');
  const filled = known.filter((f) => f.fill === 'filled').length;
  const hidden = data.facts.length - known.length;
  const captures = [...new Set(data.platforms.map((p) => `${p.platform} ${p.capture}`))].join(' + ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 12px 16px' }} data-testid="session-overview">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: 13, color: 'var(--ink-2)' }} data-testid="overview-summary">
        <span><b style={{ color: 'var(--ink)' }}>{data.facts.length}</b> slots · {visit ? <b style={{ color: 'var(--ink)' }}>{visit.label}</b> : <><b style={{ color: 'var(--ink)' }}>{visits.length}</b> page visits</>}</span>
        <span>fill <b style={{ color: 'var(--ink)' }}>{known.length ? pct(filled / known.length) : 'n/a'}</b>{known.length ? ` of ${known.length} known` : ''}</span>
        {hidden ? <span><b style={{ color: 'var(--ink)' }}>{hidden}</b> not visible</span> : null}
        <span>{captures} · {clock(data.start, false)}–{clock(data.end, false)}</span>
      </div>

      {visits.length > 1 ? <PageChips visits={visits} total={(lifecycles || []).length} selected={visit ? visit.key : null} onSelect={setSelVisit} /> : null}

      {data.platforms.length > 1 ? <PlatformCards platforms={data.platforms} /> : null}

      <section style={panel}>
        <div style={headRow}>
          <h2 style={h2}>Slot timeline</h2>
          <Legend items={[['Filled', 'var(--success-text)'], ['No fill', 'var(--error-text)'], ['Not visible (USB)', 'var(--muted)', 'hollow'], ['×N duplicate burst', 'var(--error-text)', 'sq']]} />
        </div>
        <Timeline data={data} visits={visit ? [visit] : visits} onShowSlot={onShowSlot} onSelectVisit={setSelVisit} />
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 14 }}>
        <section style={panel}>
          <div style={headRow}>
            <h2 style={h2}>Who won</h2>
            <Legend items={usedBuckets.map((b) => [WIN_STYLE[b].label, WIN_STYLE[b].color, 'sq'])} />
          </div>
          <WhoWon wins={data.wins} />
        </section>
        <section style={panel}>
          <div style={headRow}><h2 style={h2}>Demand funnel</h2></div>
          <Funnel funnel={funnel} />
        </section>
      </div>

      <section style={panel}>
        <div style={headRow}><h2 style={h2}>Findings</h2></div>
        <Findings findings={findings} onShowSlot={onShowSlot} />
      </section>

      {hasMisses ? (
        <section style={panel}>
          <div style={headRow}><h2 style={h2}>Screen expectation misses</h2></div>
          <ExpectationMisses misses={expectationMisses} />
        </section>
      ) : null}
    </div>
  );
}
